import { type ComponentProps, useCallback, useMemo } from 'react';

import type { CheckoutAdditionalFieldsForm } from '#pages/Checkout/checkout.types.js';
import { getCartIdsFromCheckout } from '#pages/Checkout/utils.js';
import { useShowCauseW_Selector } from '@givebacks/api/core';
import { type ConsumerCheckoutCartsResponse, useShowConsumerCheckout } from '@givebacks/api/store';
import { useForm } from '@givebacks/formy';
import { Text } from 'ui';
import { AdditionalFieldsFormsGuard } from 'ui/Core';

type CheckoutAdditionalFieldsProps = {
  checkoutId: string;
  cartIds: string[];
};
type CheckoutAdditionalFieldsGuardProps = {
  checkoutId: string;
};

const getCartFromCartId = (cartId: string, data?: ConsumerCheckoutCartsResponse) => {
  return data?.checkout.carts.find((cart) => cart.uuid === cartId);
};

const CartAdditionalFields = ({
  cartId,
  checkoutId,
  withCauseName = true,
  onChange,
}: {
  cartId: string;
  checkoutId: string;
  withCauseName?: boolean;
  onChange: ComponentProps<typeof AdditionalFieldsFormsGuard>['onChange'];
}) => {
  const { data: cart } = useShowConsumerCheckout({
    checkoutId,
    select: useCallback(
      (data?: ConsumerCheckoutCartsResponse) => getCartFromCartId(cartId, data),
      [cartId]
    ),
  });
  const { data: cause } = useShowCauseW_Selector({ uuid: cart?.cause_id });

  const displayTitle = useMemo(() => {
    if (withCauseName && cause?.name) {
      return (
        <Text fz={16} fw="600" c="gray.8">
          Additional Fields{' '}
          <Text span fw="400">
            (for {cause?.name})
          </Text>
        </Text>
      );
    }

    return 'Additional Fields';
  }, [withCauseName, cause?.name]);

  return (
    <AdditionalFieldsFormsGuard
      title={displayTitle}
      excludedOptions={['address']}
      options={cart?.cart_options}
      formId={`additional_fields_${cartId}`}
      onChange={onChange}
    />
  );
};

const CheckoutAdditionalFields = ({ checkoutId, cartIds }: CheckoutAdditionalFieldsProps) => {
  const form = useForm<CheckoutAdditionalFieldsForm>(
    {
      initialValues: {
        carts: {},
      },
    },
    { id: 'checkout_additional_fields' }
  );

  const withCauseName = useMemo(() => {
    return Boolean(cartIds?.length && cartIds.length > 1);
  }, [cartIds]);

  const additionalFieldsForms = useMemo(() => {
    return cartIds?.map((cartId) => (
      <CartAdditionalFields
        key={cartId}
        cartId={cartId}
        checkoutId={checkoutId}
        withCauseName={withCauseName}
        onChange={(_options, formattedValues) => {
          form.setFieldValue(`carts.${cartId}`, formattedValues.asObject);
        }}
      />
    ));
  }, [cartIds, checkoutId, withCauseName, form]);

  return <>{additionalFieldsForms}</>;
};

/**
 * Ensures that the additional fields forms are only rendered when there are cart id's returned from the API
 */
const CheckoutAdditionalFieldsGuard = ({ checkoutId }: CheckoutAdditionalFieldsGuardProps) => {
  const { data: cartIds } = useShowConsumerCheckout({
    checkoutId: checkoutId,
    select: getCartIdsFromCheckout,
  });
  if (!cartIds) return null;
  return <CheckoutAdditionalFields checkoutId={checkoutId} cartIds={cartIds} />;
};

export { CheckoutAdditionalFieldsGuard as CheckoutAdditionalFields };
